<template>
	<div v-show="displayValidationRunning">Running Validation Check...</div>
	<div v-show="!displayValidationRunning">
		<!-- Page Label with Selected Option Name -->
		<PageLabel :helpPageName="'Assessments' + props.tabName.replace(/\s+/g, '') + props.subTabName.replace(/\s+/g, '')">
			{{ isMultiOptionFeatureEnabled && calculatedOptionName ? "Results for " + calculatedOptionName : "Results" }}
		</PageLabel>

		<div v-show="!displayResult && displayWarning">
			<warningBlock :warningMessages="warningMessage" />
		</div>

		<!-- Two column layout -->
		<div class="flex flex-row gap-4 mb-4">
			<!-- Left column: Tabs -->
			<div class="flex-[3] min-w-0">
				<div v-show="resultsAvailable" class="i2Panel">
					<div v-show="displayResult">
						<Tabs value="0">
							<TabList @click="changeTab">
								<Tab value="0">
									<FontAwesomeIcon :icon="['far', 'arrows-spin']" title="Results by Life Cycle Stage" />
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Life Cycle Stage" : "Results by Life Cycle (Summary)" }}
									</span>
								</Tab>
								<Tab value="1"
									><FontAwesomeIcon
										:icon="props.tabName === tabsDefinitions.packaging.value ? ['far', 'boxes-packing'] : ['far', 'arrows-spin']"
										title="Results by Packaging Type"
									/>
									<span class="ml-2">
										{{ props.tabName === tabsDefinitions.packaging.value ? "Results by Packaging Type" : "Results by Life Cycle (Detailed)" }}
									</span></Tab
								>
								<Tab value="2"
									><FontAwesomeIcon :icon="['far', 'flask-round-potion']" title="Results by Material Composition" /><span class="ml-2"
										>Results by Material Composition</span
									></Tab
								>
								<Tab value="3"
									><FontAwesomeIcon :icon="['far', 'trash-can-list']" title="Results by Consumer waste - End of Life" /><span class="ml-2"
										>Results by Consumer waste - End of Life</span
									></Tab
								>
							</TabList>
							<TabPanels>
								<TabPanel value="0"
									><assessmentTabResultByLifeCycleStage
										ref="resultByLifeCycleStageControl"
										:assessmentId="assessmentId"
										:tabId="props.tabId"
										:finalResult="finalResult"
										:tabName="props.tabName"
										:activeEmissionFactors="reportsStore.selectedEmissionFactors"
										:isWeighted="isWeighted"
										:referenceUnitName="referenceUnitName"
										:calculateResultsBy="calculateResultsByLabel"
										:calculatedQuantity="calculatedQuantity"
										:calculatedFormatLabel="calculatedFormatLabel"
									/> </TabPanel
								><TabPanel value="1"
									><assessmentTabResultByPackagingType
										ref="resultByPackagingTypeControl"
										:assessmentId="assessmentId"
										:tabId="props.tabId"
										:tabName="props.tabName"
										:finalResult="finalResult"
										:activeEmissionFactors="reportsStore.selectedEmissionFactors"
										:isWeighted="isWeighted"
										:referenceUnitName="referenceUnitName"
										:calculateResultsBy="calculateResultsByLabel"
										:calculatedQuantity="calculatedQuantity"
										:calculatedFormatLabel="calculatedFormatLabel"
									/> </TabPanel
								><TabPanel value="2"
									><assessmentTabResultByMaterialComposition
										ref="resultByMaterialCompositionControl"
										:assessmentId="assessmentId"
										:tabId="props.tabId"
										:finalResult="finalResult"
										:tabName="props.tabName"
										:activeEmissionFactors="reportsStore.selectedEmissionFactors"
										:isWeighted="isWeighted"
										:referenceUnitName="referenceUnitName"
										:calculateResultsBy="calculateResultsByLabel"
										:calculatedQuantity="calculatedQuantity"
										:calculatedFormatLabel="calculatedFormatLabel"
									/> </TabPanel
								><TabPanel value="3"
									><assessmentTabResultByProcessWaste
										ref="resultByProcessWasteControl"
										:assessmentId="assessmentId"
										:tabId="props.tabId"
										:finalResult="finalResult"
										:tabName="props.tabName"
										:activeEmissionFactors="reportsStore.selectedEmissionFactors"
										:isWeighted="isWeighted"
										:referenceUnitName="referenceUnitName"
										:calculateResultsBy="calculateResultsByLabel"
										:calculatedQuantity="calculatedQuantity"
										:calculatedFormatLabel="calculatedFormatLabel" /></TabPanel
							></TabPanels>
						</Tabs>
					</div>
				</div>
			</div>

			<!-- Right column: Buttons + SelectableList -->
			<div class="flex-[1] min-w-0 flex flex-col gap-4 mt-3">
				<!-- Buttons -->
				<div class="flex flex-row items-end justify-end gap-4">
					<Button class="btn p-button" @click="loadResults" :disabled="isCalculateDisabled">{{
						resultsAvailable ? "Recalculate" : "Calculate"
					}}</Button>
					<Button v-show="resultsAvailable" class="btn p-button" @click="exportCalculationLog()">Log</Button>
					<Button
						v-show="resultsAvailable"
						class="btn p-button"
						@click="exportToBody()"
						:disabled="reportsStore.selectedEmissionFactorNames.length === 0"
						v-tooltip.top="
							reportsStore.selectedEmissionFactorNames.length === 0
								? 'Please select at least one emission factor to export'
								: 'Export results to Excel'
						"
					>
						Export
					</Button>
				</div>
				<!-- GeneralInformation Option Selector for Packaging/Distribution -->
				<div
					v-if="
						isMultiOptionFeatureEnabled &&
						generalInformationOptions.length > 1 &&
						(props.tabName === tabsDefinitions.packaging.value || props.tabName === tabsDefinitions.distribution.value)
					"
					class="flex flex-row items-end justify-end gap-4"
				>
					<I2FormField :label="props.tabName + ' Option'">
						<Select
							v-model="selectedResultGeneralInformationId"
							:options="generalInformationOptions"
							optionLabel="generalInformationName"
							optionValue="id"
							placeholder="Select Option"
						/>
					</I2FormField>
				</div>
				<!-- Packaging Result Options -->
				<div v-if="props.tabName === tabsDefinitions.packaging.value" class="flex flex-row items-end justify-end gap-4">
					<I2FormField label="Quantity">
						<InputNumber v-model="resultQuantity" :min="1" />
					</I2FormField>
				</div>
				<div v-if="props.tabName === tabsDefinitions.packaging.value" class="flex flex-row items-end justify-end gap-4">
					<I2FormField label="Format">
						<Select
							v-model="resultFormat"
							:options="resultFormatOptionsWithLabels"
							optionLabel="displayLabel"
							dataKey="id"
							placeholder="Select Format"
						/>
					</I2FormField>
				</div>

				<!-- Weighted Toggle -->
				<div v-if="weightedOptionEnabled" class="flex items-center gap-2">
					<ToggleSwitch v-model="isWeighted" inputId="weightedToggle" />
					<label for="weightedToggle" class="font-medium">Weighted</label>
				</div>

				<!-- SelectableList -->
				<SelectableList
					v-model="reportsStore.selectedEmissionFactorNames"
					:options="reportsStore.emissionFactors"
					option-label="emissionFactorName"
					option-value="emissionFactorName"
					placeholder="Select Emission Factors"
					:filter="true"
				/>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import { ref, onMounted, onActivated, nextTick, watch, computed, inject, type Ref } from "vue"
import Tabs from "primevue/tabs"
import TabList from "primevue/tablist"
import Tab from "primevue/tab"
import TabPanels from "primevue/tabpanels"
import TabPanel from "primevue/tabpanel"
import commonTabs from "./commonTabs.vue"
import Button from "primevue/button"
import ToggleSwitch from "primevue/toggleswitch"
import InputNumber from "primevue/inputnumber"
import Select from "primevue/select"
import {
	ApiClient,
	FileResponse,
	FinalResult,
	ExportRequest,
	FeatureFlagClient,
	GeneralInformation,
	MasterDropdown,
	ProductFormatClient,
} from "../../entities/NSwagGenerated"
import { EventBus } from "@/eventBus"
import { library } from "@fortawesome/fontawesome-svg-core"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faFloppyDisk,
	faXmarkLarge,
	faArrowsSpin,
	faBoxesPacking,
	faFlaskRoundPotion,
	faTrashCanList,
	faSpinner,
} from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
library.add(faTrash, faPen, faInfoCircle, faFloppyDisk, faXmarkLarge, faArrowsSpin, faBoxesPacking, faFlaskRoundPotion, faTrashCanList, faSpinner)

import { useErrorHandler } from "../composables/useErrorHandler"
import assessmentTabResultByLifeCycleStage from "./resultByLifeCycleStageDPAG.vue"
import assessmentTabResultByPackagingType from "./resultByPackagingTypeDPAG.vue"
import assessmentTabResultByMaterialComposition from "./resultByMaterialCompositionDPAG.vue"
import assessmentTabResultByProcessWaste from "./resultByProcessWasteDPAG.vue"

import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { useErrorToaster, useToaster, SystemToastOptions } from "@/composables/toaster"
import { ErrorFormat } from "@/models/errorFormat"
import SelectableList from "./SelectableList.vue"
import { useRoute } from "vue-router"
import { useReportsStore } from "@/store/reportsStore"
import Card from "primevue/card"
import PageLabel from "./pageLabel.vue"
import warningBlock from "./warningBlock.vue"
import I2FormField from "@/crispify/crispy-components/I2FormField.vue"
const tabsDefinitions = useTabsDefinitions()

const displayResult = ref(true)
const displayValidationRunning = ref(false)
const displayWarning = ref(false)
const warningMessage = ref<string[]>([])

const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const featureFlagClient = new FeatureFlagClient(import.meta.env.VITE_API_URL?.toString())

const route = useRoute()
const props = defineProps({
	assessmentId: {
		type: String,
		required: false,
		default: "",
	},
	tabId: {
		type: String,
		required: false,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	subTabName: {
		type: String,
		required: false,
		default: "",
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
})

//TAB STATUS
class tabActualStatus {
	actualMainTabId: string
	actualSubTabName: string
	selectedStepId: string
	generalInformationId: string
}

const finalResult = ref({} as FinalResult)

// child component refs
const resultByLifeCycleStageControl = ref<InstanceType<typeof assessmentTabResultByLifeCycleStage>>()
const resultByPackagingTypeControl = ref<InstanceType<typeof assessmentTabResultByPackagingType>>()
const resultByMaterialCompositionControl = ref<InstanceType<typeof assessmentTabResultByMaterialComposition>>()
const resultByProcessWasteControl = ref<InstanceType<typeof assessmentTabResultByProcessWaste>>()

const loading = ref(false)
const resultsAvailable = ref(false)
const errorEOL = ref(false)
const reportsStore = useReportsStore()
const isWeighted = ref(false)
const weightedOptionEnabled = ref(false)
const selectedOptionName = ref<string>("")
const calculatedOptionName = ref<string>("")
const isMultiOptionFeatureEnabled = ref(false)
let eventDone = 0

// Calculate Results By label for Packaging tab
const calculateResultsByLabel = ref("Reference Unit")
// Reference unit name (e.g., "Tablet", "ml", "Dose") for use in child component titles
const referenceUnitName = ref("")
// Product format name from General Information
const productFormatName = ref("")

// Packaging result options - quantity and format
const resultQuantity = ref<number>(1)
const resultFormat = ref<MasterDropdown | null>(null)
const resultFormatOptions = ref<MasterDropdown[]>([])
const isCalculateDisabled = ref(false)

// Stores the quantity and format label used in the last calculation (set on Calculate/Recalculate)
const calculatedQuantity = ref<number>(1)
const calculatedFormatLabel = ref<string>("")

// GeneralInformation option selector for Packaging/Distribution
// Injected from commonTabs — stays in sync automatically when options are added/removed
const generalInformationOptions = inject<Ref<GeneralInformation[]>>("packagingOptions", ref([]))
// Shared ref with commonTabs — changes in either dropdown stay in sync automatically
const selectedResultGeneralInformationId = inject<Ref<string | null>>("selectedPackagingOption", ref(null))

// Persist selection to DB when the dropdown changes and trigger validation
watch(selectedResultGeneralInformationId, async (newId, oldId) => {
	if (!newId || newId === oldId) return
	// Persist selection in DB
	try {
		await apiClient.setGeneralInformationSelected(newId)
	} catch (error) {
		console.error("Error syncing general information selection:", error)
	}
	// Update global tab status
	const tabStatus = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)
	if (tabStatus) {
		tabStatus.generalInformationId = newId
	}
	// Reset displayed results and validate the newly selected packaging option
	resultsAvailable.value = false
	displayResult.value = false
	validateResultMatrix(newId)
})

// Watch quantity to ensure it's always at least 1
watch(resultQuantity, (newValue) => {
	if (newValue === null || newValue === undefined || newValue < 1) {
		resultQuantity.value = 1
	}
})

// Computed to generate format options with dynamic labels and filtering
const resultFormatOptionsWithLabels = computed(() => {
	return resultFormatOptions.value
		.filter((option) => {
			// If Product Format is selected in General Information, hide Reference Unit option
			if (calculateResultsByLabel.value === "Product Format" && option.valueTxt === "Reference Unit") {
				return false
			}
			// If Reference Unit is selected in General Information, hide Product Format option
			if (calculateResultsByLabel.value === "Reference Unit" && option.valueTxt === "Product Format") {
				return false
			}
			return true
		})
		.map((option) => {
			let label = option.valueTxt || ""
			// Add unit name in brackets for Product Format and Reference Unit
			if (option.valueTxt === "Product Format" && productFormatName.value) {
				label = `Product Format [${productFormatName.value}]`
			} else if (option.valueTxt === "Reference Unit" && referenceUnitName.value) {
				label = `Reference Unit [${referenceUnitName.value}]`
			}
			return {
				...option,
				displayLabel: label,
			}
		})
})

// Load result format options from database
const loadResultFormatOptions = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const options = await apiClient.masterDropdownByCategoryKey("resultFormatOption")
		// Sort by ordering field
		options.sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0))
		resultFormatOptions.value = options
	} catch (error) {
		console.error("Error loading result format options:", error)
	}
}

// Set the default format selection to first available filtered option
const setDefaultResultFormat = () => {
	const filteredOptions = resultFormatOptionsWithLabels.value
	if (filteredOptions.length === 0) return

	// Check if current selection is still valid in the filtered options
	const currentSelection = resultFormat.value
	const isCurrentSelectionValid = currentSelection && filteredOptions.some((opt) => opt.id === currentSelection.id)

	// If no selection or current selection is not valid, set to first available
	if (!currentSelection || !isCurrentSelectionValid) {
		resultFormat.value = filteredOptions[0] || null
	}
}

// Watch for changes in calculateResultsByLabel to update the selection when it changes
watch(calculateResultsByLabel, () => {
	setDefaultResultFormat()
})

// Load selected option name from general information
const loadSelectedOptionName = async (giId: string) => {
	try {
		// Load all generalInformation options for this assessment/screen and find the one with matching ID
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		const gi = options.find((opt) => opt.id === giId)
		if (gi) {
			selectedOptionName.value = gi.generalInformationName || gi.versionName || ""
		}
	} catch (error) {
		console.error("Error loading general information name:", error)
		selectedOptionName.value = ""
	}
}

// Load Calculate Results By type from General Information for Packaging tab
const loadCalculateResultsByType = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		const result = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		if (result && result.length > 0) {
			const packagingGI = result.find((item: any) => !item.notes?.startsWith("consumerEndOfLife_"))
			if (packagingGI) {
				// If packReferenceUnit has a value, it's "Reference Unit"
				// If productFormatId has a value, it's "Product Format"
				if (packagingGI.packReferenceUnit != null && packagingGI.packReferenceUnit.id != null) {
					calculateResultsByLabel.value = "Reference Unit"
					// Store the unit name for child components
					referenceUnitName.value = packagingGI.packReferenceUnit.valueTxt || ""
					productFormatName.value = ""
				} else if (packagingGI.productFormatId != null && packagingGI.productFormatId !== "") {
					calculateResultsByLabel.value = "Product Format"
					referenceUnitName.value = ""
					// Load the product format name
					try {
						const productFormatClient = new ProductFormatClient(import.meta.env.VITE_API_URL?.toString())
						const productFormats = await productFormatClient.list()
						const productFormat = productFormats.find((pf) => pf.id === packagingGI.productFormatId)
						productFormatName.value = productFormat?.formatName || ""
					} catch {
						productFormatName.value = ""
					}
				}
			}
		}
	} catch (error) {
		// Default to Reference Unit if there's an error
		calculateResultsByLabel.value = "Reference Unit"
		referenceUnitName.value = ""
		productFormatName.value = ""
	}
}

// Load generalInformation options for the Option dropdown (Packaging/Distribution)
const loadGeneralInformationOptions = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value && props.tabName !== tabsDefinitions.distribution.value) {
		return
	}
	if (!isMultiOptionFeatureEnabled.value) {
		return
	}

	try {
		const options = await apiClient.getCommonAPIGeneralByAssessmentIdAndScreenId(props.assessmentId, props.tabId)
		let filtered = options.filter((gi: any) => gi.isActive === true)
		// For Packaging, exclude consumerEndOfLife_ internal records
		if (props.tabName === tabsDefinitions.packaging.value) {
			filtered = filtered.filter((gi: any) => !gi.notes || !gi.notes.includes("consumerEndOfLife_"))
		}
		generalInformationOptions.value = filtered

		// Pre-select: find wasSelected, or fall back to first
		if (!selectedResultGeneralInformationId.value || !filtered.some((gi: any) => gi.id === selectedResultGeneralInformationId.value)) {
			const selected = filtered.find((gi: any) => gi.wasSelected === true) || filtered[0]
			selectedResultGeneralInformationId.value = selected?.id ?? null
		}
	} catch (error) {
		console.error("Error loading general information options:", error)
		generalInformationOptions.value = []
	}
}

// Check EOL destination setting for packaging tab
const checkEolDestinationSetting = async () => {
	if (props.tabName !== tabsDefinitions.packaging.value) {
		return
	}

	try {
		// Get the current packaging generalInformationId if available
		const packagingGiId =
			selectedResultGeneralInformationId.value ||
			props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

		// Pass packagingGeneralInformationId to check the correct Consumer End Of Life record
		const result = await apiClient.checkIfDestinationMarketSelected(props.assessmentId, packagingGiId || undefined)

		// If successful, remove any previous EOL warning and enable Calculate button
		warningMessage.value = warningMessage.value.filter((msg) => !msg.includes("Destination") && !msg.includes("Consumer End Of Life"))
		isCalculateDisabled.value = false
		displayWarning.value = warningMessage.value.length > 0
	} catch (err: any) {
		console.log("checkEolDestinationSetting: API returned error", err)
		console.log("checkEolDestinationSetting: err.result", err?.result)
		// If error, add warning message and disable Calculate button
		// NSwag stores the response body in err.result for 400 errors
		const errorMessage =
			err?.result?.message ||
			err?.response?.message ||
			"Please open 'Consumer End Of Life' tab and select the Destination Setting field, then click Save"
		// Check if this warning is not already present
		if (!warningMessage.value.some((msg) => msg.includes("Destination") || msg.includes("Consumer End Of Life"))) {
			warningMessage.value.push(errorMessage)
		}
		displayResult.value = false
		displayWarning.value = true
		isCalculateDisabled.value = true
	}
}

onMounted(async () => {
	// Check if weighted calculation option is enabled
	const weightedFlag = await featureFlagClient.getByName("Weighted Calculation Option")
	weightedOptionEnabled.value = weightedFlag?.isEnabled || false

	// Check if multi-option feature flag is enabled
	try {
		const multiOptionFlag = await featureFlagClient.getByName("Allow Multiple Packaging Options per Assessment")
		isMultiOptionFeatureEnabled.value = multiOptionFlag?.isEnabled || false
	} catch (error) {
		isMultiOptionFeatureEnabled.value = false
	}
	// Load result format options for Packaging tab
	await loadResultFormatOptions()

	// Load Calculate Results By type for Packaging tab
	await loadCalculateResultsByType()

	// Set default format selection after both options and type are loaded
	setDefaultResultFormat()

	// Load generalInformation options for Option dropdown
	await loadGeneralInformationOptions()

	// Check EOL destination setting for packaging tab
	await checkEolDestinationSetting()

	// Event listener for result validation triggered from parent
	EventBus.on("validateResult_" + props.tabId, async (data: any) => {
		if (data != null && data.generalInformationId != null) {
			// Only run validation if generalInformationId changed or results are not available
			const giChanged = selectedResultGeneralInformationId.value !== data.generalInformationId

			if (giChanged || !resultsAvailable.value) {
				selectedResultGeneralInformationId.value = data.generalInformationId

				// Only run validation if results are not already available
				if (!resultsAvailable.value) {
					displayValidationRunning.value = true
					validateResultMatrix(data.generalInformationId)
				}

				// Load the selected option name if it changed
				if (giChanged || !selectedOptionName.value) {
					await loadSelectedOptionName(data.generalInformationId)
				}
			}
			selectedResultGeneralInformationId.value = data.generalInformationId
			displayValidationRunning.value = true
			// Re-load Calculate Results By type when navigating back to Result tab
			await loadCalculateResultsByType()
			// Update default format selection
			setDefaultResultFormat()
			// Reload generalInformation options for Option dropdown
			await loadGeneralInformationOptions()
			// Re-check EOL destination setting when navigating back to Result tab
			await checkEolDestinationSetting()
			validateResultMatrix(data.generalInformationId)
		}
	})

	// Event listener to force reset results (e.g., when GeneralInformation option changes)
	EventBus.on("forceResetResults_" + props.tabId, () => {
		resultsAvailable.value = false
		displayResult.value = false
		finalResult.value = {} as FinalResult
	})

})

// Re-check EOL destination setting when component is reactivated (keep-alive)
onActivated(async () => {
	// Re-load Calculate Results By type when component is reactivated
	await loadCalculateResultsByType()
	// Update default format selection
	setDefaultResultFormat()
	// Reload generalInformation options for Option dropdown
	await loadGeneralInformationOptions()
	await checkEolDestinationSetting()
})

const status = ref()

const checkDestinationMarketSetting = () => {
	try {
		// Get the current packaging generalInformationId if available
		const packagingGiId =
			selectedResultGeneralInformationId.value ||
			props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId
		const check = apiClient.checkIfDestinationMarketSelected(props.assessmentId, packagingGiId || undefined)
	} catch (err: any) {
		useErrorHandler().setErrorHandled(err)

		useErrorToaster(err.response)
	}
}

const validateResultMatrix = (generalInformationId: string) => {
	if (generalInformationId) {
		displayValidationRunning.value = true
		apiClient.validateResultMatrixByGeneralInformationId(generalInformationId).then(
			(res) => {
				displayValidationRunning.value = false
				if (res != null && res.length > 0) {
					// Only reset displayResult if results are not already available
					if (!resultsAvailable.value) {
						displayResult.value = false
					}
					// Extract messages using ValidationMessage properties
					const warnings = res.filter((item) => item.messageType === "Warning").map((item) => item.messageText!)
					const errors = res.filter((item) => item.messageType === "Error").map((item) => item.messageText!)
					// Preserve EOL messages and combine with new warnings and errors
					const eolMessages = warningMessage.value.filter((msg) => msg.includes("Destination Market") || msg.includes("Consumer End Of Life"))
					warningMessage.value = [...eolMessages, ...warnings, ...errors]
					displayWarning.value = warningMessage.value.length > 0
				} else {
					// Validation passed - don't reset displayResult if results are already available
					if (!resultsAvailable.value) {
						displayResult.value = false
					}

					// Preserve EOL messages BEFORE clearing the array
					const eolMessages = warningMessage.value.filter((msg) => msg.includes("Destination Market") || msg.includes("Consumer End Of Life"))
					warningMessage.value = [...eolMessages]
					displayWarning.value = warningMessage.value.length > 0
				}
			},
			(err) => {
				displayValidationRunning.value = false
				if (err.status == 400) {
					// Only reset displayResult if results are not already available
					if (!resultsAvailable.value) {
						displayResult.value = false
					}
					console.error("Error Id:", err.response.errorId)
					// Preserve EOL messages
					const eolMessages = warningMessage.value.filter((msg) => msg.includes("Destination Market") || msg.includes("Consumer End Of Life"))
					warningMessage.value = [...eolMessages, err.response.message]
					displayWarning.value = true
				} else {
					useToaster({ severity: "error", summary: "Error", detail: `Validation error: ${err.message}`, life: 2000 })
				}
			}
		)
	}
}

// Populate process waste tables before calculating results
async function populateProcessWaste(): Promise<void> {
	const actualTab = props.allTabsInformation?.allTabsStatus?.find((item: any) => item.actualMainTabId === props.tabId)

	if (!actualTab) return

	const genInfoId = actualTab.generalInformationId
	const stepId = actualTab.selectedStepId

	// Populate processWaste for the current step
	if (genInfoId && stepId) {
		try {
			await apiClient.populateProcessWasteAndTable(
				props.assessmentId,
				genInfoId,
				props.tabId,
				stepId,
				"actual", // Load actual saved data
				undefined // region - only needed for EOL
			)
		} catch (err) {
			console.warn("Failed to populate process waste, continuing with calculation:", err)
			// Do not block calculation if population fails
		}
	}
}

const loadResults = async () => {
	// Calculate button simply loads results without validation
	// Validation already happened on page load via EventBus
	resultsAvailable.value = false
	loading.value = true
	displayResult.value = true

	const actualTab = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)
	if (props.subTabName.replace(/\s+/g, "") === actualTab?.actualSubTabName && route.query.assessmentId === props.assessmentId) {
		eventDone++
		// For packaging tab, pass quantity and format type parameters
		const quantity = props.tabName === tabsDefinitions.packaging.value ? resultQuantity.value : undefined
		const formatType = props.tabName === tabsDefinitions.packaging.value ? resultFormat.value?.valueTxt : undefined

		try {
			// Populate process waste tables before calculating results
			await populateProcessWaste()

			// Then calculate results - pass selectedResultGeneralInformationId if available
			const giIdParam = selectedResultGeneralInformationId.value || undefined
			const result = await apiClient.getPackResults(
				props.assessmentId,
				props.tabName,
				reportsStore.selectedEmissionFactorNames,
				isWeighted.value,
				quantity,
				formatType,
				giIdParam
			)

			finalResult.value = result
			resultsAvailable.value = true
			loading.value = false
			errorEOL.value = false

			// Capture the quantity and format used for this calculation
			calculatedQuantity.value = quantity ?? 1
			const matchedOption = resultFormatOptionsWithLabels.value.find((opt) => opt.id === resultFormat.value?.id)
			calculatedFormatLabel.value = matchedOption?.displayLabel || resultFormat.value?.valueTxt || calculateResultsByLabel.value || ""

			// Store the name of the generalInformation used for this calculation
			if (isMultiOptionFeatureEnabled.value && giIdParam) {
				const option = generalInformationOptions.value.find((gi: any) => gi.id === giIdParam)
				calculatedOptionName.value = option?.generalInformationName || ""
			} else {
				calculatedOptionName.value = ""
			}
		} catch (err: any) {
			useErrorHandler().setErrorHandled(err.response)
			let msg: SystemToastOptions = {
				severity: "warn",
				summary: "Warning",
				detail: err.response?.message || err.message,
				life: 5000,
			}

			useToaster(msg)
			errorEOL.value = err.response?.message?.toLowerCase().includes("destination market")
			loading.value = false
			resultsAvailable.value = false
		}
	}
}

watch(
	isWeighted,
	() => {
		if (displayResult.value) {
			loadResults()
		}
	},
	{ flush: "post" }
)

// function exportCalculationLog() {
// 	let generalInfomationId = props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

// 	let downloadCalculationLog = (res: FileResponse) => {
// 		let fileURL = window.URL.createObjectURL(new Blob([res.data]))
// 		let fileLink = document.createElement("a")

// 		fileLink.href = fileURL
// 		let fileName = `Calculation_Log`
// 		fileLink.setAttribute("download", fileName + ".xlsx")
// 		document.body.appendChild(fileLink)
// 		fileLink.click()

// 		window.URL.revokeObjectURL(fileURL)
// 		document.body.removeChild(fileLink)
// 	}
// 	let errorCalculationLog = (error: FileResponse) => {
// 		status.value = error
// 	}

// 	console.log(props.tabName)
// 	if (props.tabName === "Distribution") apiClient.getDistributionCalculationLog(generalInfomationId).then(downloadCalculationLog, errorCalculationLog)
// 	else apiClient.getDnpCalculationLog(generalInfomationId).then(downloadCalculationLog, errorCalculationLog)
// }

function exportCalculationLog() {
	let generalInfomationId =
		selectedResultGeneralInformationId.value ||
		props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

	let downloadCalculationLog = (res: FileResponse) => {
		let fileURL = window.URL.createObjectURL(new Blob([res.data]))
		let fileLink = document.createElement("a")

		fileLink.href = fileURL
		let fileName = `Calculation_Log`
		fileLink.setAttribute("download", fileName + ".xlsx")
		document.body.appendChild(fileLink)
		fileLink.click()

		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)
	}
	let errorCalculationLog = (error: FileResponse) => {
		status.value = error
	}

	if (props.tabName === "Distribution")
		apiClient
			.getDistributionCalculationLog(generalInfomationId, reportsStore.selectedEmissionFactorNames, isWeighted.value)
			.then(downloadCalculationLog, errorCalculationLog)
	else
		apiClient
			.getDnpCalculationLog(generalInfomationId, reportsStore.selectedEmissionFactorNames, isWeighted.value)
			.then(downloadCalculationLog, errorCalculationLog)
}

const exportToBody = async () => {
	// Validazione
	if (reportsStore.selectedEmissionFactorNames.length === 0) {
		useToaster({
			severity: "warn",
			summary: "Warning",
			detail: "Please select at least one emission factor to export",
			life: 5000,
		})
		return
	}

	try {
		const assessment = await apiClient.getSingleAssessmentById(props.assessmentId)

		// Get the generalInformationId - use selected dropdown, fallback to tab information
		const giId =
			selectedResultGeneralInformationId.value ||
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === props.tabId)?.generalInformationId

		// Use API client with new payload structure
		const exportRequest = new ExportRequest({
			finalResult: finalResult.value,
			emissionFactorNames: reportsStore.selectedEmissionFactorNames,
			isWeighted: isWeighted.value,
			generalInformationId: giId,
			// Pass additional parameters for Packaging export summary sheet
			assessmentId: props.assessmentId,
			quantity: props.tabName === tabsDefinitions.packaging.value ? resultQuantity.value : undefined,
			formatType: props.tabName === tabsDefinitions.packaging.value ? resultFormat.value?.valueTxt : undefined,
		})
		const response = await apiClient.downloadReportPackagingDistribution(props.tabName, exportRequest)

		// Download file - response is a FileResponse with data: Blob
		const blob = response.data
		const fileURL = window.URL.createObjectURL(blob)
		const fileLink = document.createElement("a")
		fileLink.href = fileURL

		// Use the fileName from the response if available, otherwise use the generated one
		const fileName = response.fileName || `${props.tabName}_${assessment.assessmentName}_${Date.now()}.xlsx`
		fileLink.setAttribute("download", fileName)
		document.body.appendChild(fileLink)
		fileLink.click()

		// Cleanup
		window.URL.revokeObjectURL(fileURL)
		document.body.removeChild(fileLink)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "Report exported successfully",
			life: 3000,
		})
	} catch (err: any) {
		console.error("Export error:", err)
		useErrorToaster(err)
	}
}

async function changeTab() {
	await nextTick()
	// Chart data is now loaded when user manually selects impact category
}
</script>
